/** Mirrors GP ConsolidatedReport / getscanned ordering and filtering. */

import {
  detectReportStatusEra,
  FBS_CONSOLIDATED_FINDING_FILTERS_V2,
  getSystemStatusDisplay,
  gpStatusReviewCompleted,
  itemMatchesConsolidatedFindingFilter,
  resolveExplicitFbsV2Flag,
  resolveFbsV2Display,
  shouldShowConsolidatedCondition
} from './fbsFindingStatuses';

export interface FbsReportItem {
  id?: number;
  condition?: string;
  status?: string;
  statusKey?: string;
  newStatus?: string;
  new_status?: string;
  oldStatus?: string;
  old_status?: string;
  description?: string | null;
  detailedNote?: string | null;
  detailed_note?: string | null;
}

export interface FbsReportOrgan {
  name?: string;
  organDescription?: string | null;
  organStatus?: string;
  items?: FbsReportItem[];
}

export interface FbsReportSystem {
  name?: string;
  status?: string;
  variant?: string;
  systemNote?: string;
  /** Backend / JSON often uses snake_case */
  system_note?: string;
  organs?: Record<string, FbsReportOrgan>;
  isComplete?: boolean;
}

/** Resolves system-level notes from camelCase or snake_case (and a few legacy keys). */
export function pickSystemNoteFromSystem(system: FbsReportSystem | Record<string, unknown>): string {
  const raw = system as Record<string, unknown>;
  const keys = ['systemNote', 'system_note', 'SystemNote', 'system_notes', 'systemNotes'];
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export type FbsReportData = Record<string, FbsReportSystem>;

export const SYSTEM_ORDER = [
  'Nervous System',
  'Chest',
  'Ear, nose and throat',
  'Abdomen',
  'Pelvis and genito-urinary system',
  'Breast',
  'Endocrine',
  'Musculoskeletal'
];

export function sortSystemEntries(entries: [string, FbsReportSystem][]): [string, FbsReportSystem][] {
  return [...entries].sort((a, b) => {
    const nameA = a[1].name || a[0];
    const nameB = b[1].name || b[0];
    const indexA = SYSTEM_ORDER.indexOf(nameA);
    const indexB = SYSTEM_ORDER.indexOf(nameB);
    if (indexA === -1 && indexB === -1) return String(nameA).localeCompare(String(nameB));
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

export interface FindingFilter {
  key: string;
  label: string;
  /** Legacy: status label string. V2: status key (e.g. critical_finding). */
  id: string;
}

export const LEGACY_FINDING_FILTERS: FindingFilter[] = [
  { key: 'important', label: 'Important findings detected', id: 'Important finding detected' },
  { key: 'incidental', label: 'Incidental findings - Assess', id: 'Incidental findings - Assess' },
  {
    key: 'seen_not_significant',
    label: 'Seen, not clinically significant',
    id: 'Seen, not clinically significant'
  }
];

/** @deprecated Use getFindingFilters(bookingUsesV2) */
export const FINDING_FILTERS = LEGACY_FINDING_FILTERS.map((f) => ({
  key: f.key,
  label: f.label,
  status: f.id
}));

export function getFindingFilters(bookingUsesV2 = false): FindingFilter[] {
  if (bookingUsesV2) {
    return FBS_CONSOLIDATED_FINDING_FILTERS_V2.map((f) => ({
      key: f.id,
      label: f.label,
      id: f.id
    }));
  }
  return LEGACY_FINDING_FILTERS;
}

export function resolveUsesFbsFindingStatusV2(
  scanData: Record<string, unknown> | null | undefined,
  reportData?: FbsReportData
): boolean {
  const sources = getScanNestedSources(scanData || {});

  // GP review completion always uses v2 labels/keys (matches getscanned fbsFindingStatuses.js).
  if (sources.some((src) => gpStatusReviewCompleted(src))) {
    return true;
  }

  const era =
    reportData && Object.keys(reportData).length > 0
      ? detectReportStatusEra(reportData)
      : 'unknown';

  for (const src of sources) {
    const explicit = resolveExplicitFbsV2Flag(src);
    if (explicit === false) return false;
    if (explicit === true) {
      // Pre-v2 bookings can lack old_status but still store legacy label strings.
      if (era === 'legacy') return false;
      return true;
    }
  }

  if (era === 'v2') return true;
  return false;
}

const V2_STATUS_PRIORITY = [
  'critical_finding',
  'significant_actionable_finding',
  'indeterminate_finding',
  'benign_incidental_finding'
];

const LEGACY_STATUS_PRIORITY = LEGACY_FINDING_FILTERS.map((f) => f.id);

/** Only rows that match the consolidated report finding chips (issues), not normal/blank rows. */
export function shouldShowCondition(item: FbsReportItem, bookingUsesV2 = false): boolean {
  return shouldShowConsolidatedCondition(item, bookingUsesV2);
}

/** Highest-severity finding status among visible organ rows. */
export function resolveConsolidatedSystemStatus(
  organs: Record<string, FbsReportOrgan>,
  bookingUsesV2 = false
): string {
  const itemKeys: string[] = [];
  const itemStatuses: string[] = [];

  Object.values(organs || {}).forEach((organ) => {
    (organ.items || []).forEach((item) => {
      if (bookingUsesV2) {
        const display = resolveFbsV2Display({
          statusKey: item.statusKey || item.newStatus || item.new_status,
          statusLabel: item.status,
          status: item.status
        });
        if (display.key !== 'no_significant_abnormality' && display.key !== 'not_reported') {
          itemKeys.push(display.key);
        }
      } else {
        const s = item.status || '';
        if (LEGACY_STATUS_PRIORITY.includes(s)) {
          itemStatuses.push(s);
        }
      }
    });
  });

  if (bookingUsesV2) {
    for (const priority of V2_STATUS_PRIORITY) {
      if (itemKeys.includes(priority)) {
        return resolveFbsV2Display({ statusKey: priority }).label;
      }
    }
    return 'Not reported';
  }

  for (const priority of LEGACY_STATUS_PRIORITY) {
    if (itemStatuses.includes(priority)) return priority;
  }
  return 'Not reported';
}

export interface ConsolidatedSystemBlock {
  key: string;
  name: string;
  status: string;
  systemNote: string;
  organs: Record<string, FbsReportOrgan>;
}

export function getConsolidatedSystemsData(
  reportData: FbsReportData,
  bookingUsesV2 = false
): ConsolidatedSystemBlock[] {
  const systemEntries = Object.entries(reportData);
  const systems = systemEntries
    .map(([systemName, systemData]) => {
      const filteredOrgans: Record<string, FbsReportOrgan> = {};

      if (systemData.organs) {
        Object.entries(systemData.organs).forEach(([organKey, organData]) => {
          if (organData.items && organData.items.length > 0) {
            const filteredItems = organData.items.filter((item) =>
              shouldShowCondition(item, bookingUsesV2)
            );
            if (filteredItems.length > 0) {
              filteredOrgans[organKey] = {
                ...organData,
                items: filteredItems
              };
            }
          }
        });
      }

      if (Object.keys(filteredOrgans).length > 0) {
        const systemStatus =
          systemData.status ||
          resolveConsolidatedSystemStatus(filteredOrgans, bookingUsesV2) ||
          'Not reported';
        const displayStatus = bookingUsesV2
          ? getSystemStatusDisplay({ status: systemStatus }, true).label
          : systemStatus;

        return {
          key: systemName,
          name: systemData.name || systemName,
          status: displayStatus,
          systemNote: pickSystemNoteFromSystem(systemData),
          organs: filteredOrgans
        };
      }
      return null;
    })
    .filter((s): s is ConsolidatedSystemBlock => s !== null);

  return systems.sort((a, b) => {
    const indexA = SYSTEM_ORDER.indexOf(a.name);
    const indexB = SYSTEM_ORDER.indexOf(b.name);
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

const isShowingAllFindingFilterIds = (
  activeFilterIds: string[],
  bookingUsesV2 = false
): boolean => {
  const allIds = getFindingFilters(bookingUsesV2).map((f) => f.id);
  if (activeFilterIds.length === 0) return true;
  if (activeFilterIds.length >= allIds.length) {
    return allIds.every((id) => activeFilterIds.includes(id));
  }
  return false;
};

export function filterSystemsByStatuses(
  systems: ConsolidatedSystemBlock[],
  activeFilterIds: string[],
  bookingUsesV2 = false
): ConsolidatedSystemBlock[] {
  if (isShowingAllFindingFilterIds(activeFilterIds, bookingUsesV2)) {
    return systems;
  }

  return systems
    .map((system) => {
      const filteredOrgans = Object.entries(system.organs || {}).reduce<Record<string, FbsReportOrgan>>(
        (acc, [organKey, organData]) => {
          const matchingItems = (organData.items || []).filter((item) =>
            activeFilterIds.some((filterId) =>
              itemMatchesConsolidatedFindingFilter(item, filterId, bookingUsesV2)
            )
          );
          if (matchingItems.length > 0) {
            acc[organKey] = { ...organData, items: matchingItems };
          }
          return acc;
        },
        {}
      );
      const rollup = resolveConsolidatedSystemStatus(filteredOrgans, bookingUsesV2);
      return {
        ...system,
        organs: filteredOrgans,
        status: rollup
      };
    })
    .filter((system) => Object.keys(system.organs || {}).length > 0);
}

export function findingCountsByStatus(
  systems: ConsolidatedSystemBlock[],
  bookingUsesV2 = false
): Record<string, number> {
  return getFindingFilters(bookingUsesV2).reduce<Record<string, number>>((acc, filter) => {
    acc[filter.id] = systems.reduce((systemTotal, system) => {
      const organTotal = Object.values(system.organs || {}).reduce((organCount, organData) => {
        const matchingItems = (organData.items || []).filter((item) =>
          itemMatchesConsolidatedFindingFilter(item, filter.id, bookingUsesV2)
        );
        return organCount + matchingItems.length;
      }, 0);
      return systemTotal + organTotal;
    }, 0);
    return acc;
  }, {});
}

export const FOLLOW_UP_DISPLAY_LABELS: Record<string, string> = {
  no_further_action_or_follow_up_required: 'No further action or follow-up required',
  consider_further_follow_up: 'Consider further follow-up',
  routine_follow_up: 'Routine follow-up is recommended',
  urgent_follow_up: 'Urgent follow-up is strongly recommended'
};

export const FOLLOW_UP_REMARK_LABELS: Record<string, string> = {
  no_further_action_or_follow_up_required: 'No further action or follow-up required',
  consider_further_follow_up: 'Consider further follow-up (please specify)',
  routine_follow_up: 'Routine follow-up is recommended (please specify)',
  urgent_follow_up: 'Urgent follow-up is strongly recommended (please specify)'
};

const FOLLOW_UP_KEYS: string[] = [
  'no_further_action_or_follow_up_required',
  'consider_further_follow_up',
  'routine_follow_up',
  'urgent_follow_up'
];

export function normalizeFollowUpKey(raw: unknown): string {
  if (typeof raw === 'number' && raw >= 0 && raw < FOLLOW_UP_KEYS.length) {
    return FOLLOW_UP_KEYS[raw];
  }
  if (typeof raw === 'string') return raw;
  return '';
}

export function cleanLegacyConclusionText(text: string, labelsToTrim: string[]): string {
  if (typeof text !== 'string') return '';
  let normalized = text.trim();
  labelsToTrim
    .filter((label) => typeof label === 'string' && label.trim())
    .forEach((label) => {
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      normalized = normalized.replace(new RegExp(`^${escaped}\\s*:?\\s*`, 'i'), '').trim();
    });
  return normalized;
}

const STATUS_TAG_CLASS_TO_ROW_CLASS: Record<string, string> = {
  statusTagCriticalFinding: 'consolidated-tag-critical',
  statusTagSignificantActionableFinding: 'consolidated-tag-significant',
  statusTagIndeterminateFinding: 'consolidated-tag-inconclusive',
  statusTagBenignIncidentalFinding: 'consolidated-tag-benign',
  statusTagNoSignificantAbnormality: 'consolidated-tag-no-findings',
  statusTagNoAbnormalityDetected: 'consolidated-tag-no-abnormality',
  statusTagNotReported: 'consolidated-tag-not-reported',
  statusTagIncidentalFindingsAssess: 'consolidated-tag-incidental',
  statusTagImportantFindingDetected: 'consolidated-tag-important',
  statusTagSeenNotClinicallySignificant: 'consolidated-tag-seen'
};

/** Maps known radiology status strings to consolidated row styling (aligned with GP tag colors). */
export function getConsolidatedStatusRowClass(
  status?: string,
  bookingUsesV2 = false
): string {
  const s = status || 'Not reported';
  const legacyMap: Record<string, string> = {
    'Not reported': 'consolidated-tag-not-reported',
    'No abnormality detected': 'consolidated-tag-no-abnormality',
    'Incidental findings - Assess': 'consolidated-tag-incidental',
    'Important finding detected': 'consolidated-tag-important',
    'Seen, not clinically significant': 'consolidated-tag-seen',
    'Critical finding': 'consolidated-tag-critical',
    'Significant finding': 'consolidated-tag-significant',
    'Inconclusive finding': 'consolidated-tag-inconclusive',
    'Benign finding': 'consolidated-tag-benign',
    'No findings': 'consolidated-tag-no-findings',
    'Significant actionable finding': 'consolidated-tag-significant',
    'Indeterminate finding': 'consolidated-tag-inconclusive',
    'Benign incidental finding': 'consolidated-tag-benign',
    'No significant abnormality': 'consolidated-tag-no-findings'
  };

  if (legacyMap[s]) return legacyMap[s];

  if (bookingUsesV2) {
    const display = resolveFbsV2Display({ statusLabel: s, status: s });
    return STATUS_TAG_CLASS_TO_ROW_CLASS[display.className] || 'consolidated-tag-default';
  }

  return 'consolidated-tag-default';
}

/** Detail report status pills (maps consolidated tag classes to SCSS pill modifiers). */
export function getDetailStatusPillClass(status?: string, bookingUsesV2 = false): string {
  const rowClass = getConsolidatedStatusRowClass(status, bookingUsesV2);
  const map: Record<string, string> = {
    'consolidated-tag-important': 'status-red',
    'consolidated-tag-critical': 'status-red',
    'consolidated-tag-incidental': 'status-amber',
    'consolidated-tag-significant': 'status-amber',
    'consolidated-tag-inconclusive': 'status-yellow',
    'consolidated-tag-seen': 'status-blue',
    'consolidated-tag-benign': 'status-blue',
    'consolidated-tag-no-abnormality': 'status-green',
    'consolidated-tag-no-findings': 'status-green',
    'consolidated-tag-not-reported': 'status-default'
  };
  return map[rowClass] || 'status-default';
}

function pickScanField(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim() !== '' && String(v).trim() !== '-') return String(v).trim();
  }
  return '';
}

const SCAN_NESTED_CONTAINER_KEYS = ['data', 'booking', 'booking_details', 'booking_detail', 'scan'] as const;

/** Merge root scan payload with common nested shapes (e.g. booking details) for field lookup. */
export function getScanNestedSources(raw: Record<string, unknown>): Record<string, unknown>[] {
  const sources: Record<string, unknown>[] = [];
  const seen = new WeakSet<object>();
  const push = (obj: unknown) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;
    const o = obj as Record<string, unknown>;
    if (seen.has(o)) return;
    seen.add(o);
    sources.push(o);
  };
  push(raw);
  for (const key of SCAN_NESTED_CONTAINER_KEYS) {
    push(raw[key]);
  }
  return sources;
}

/** First non-empty string among keys, searching root then nested containers. */
export function pickStringAcrossScanSources(raw: Record<string, unknown>, keys: string[]): string {
  for (const src of getScanNestedSources(raw)) {
    const hit = pickScanField(src, keys);
    if (hit) return hit;
  }
  return '';
}

/** Booleans render as Yes/No; otherwise first non-empty string (notes or legacy Yes/No text). */
export function pickBoolOrStringAcrossScanSources(raw: Record<string, unknown>, keys: string[]): string {
  for (const src of getScanNestedSources(raw)) {
    for (const k of keys) {
      const v = src[k];
      if (typeof v === 'boolean') return v ? 'Yes' : 'No';
      if (v != null) {
        const s = String(v).trim();
        if (s !== '' && s !== '-') return s;
      }
    }
  }
  return '';
}

/** Value passed to normalizeFollowUpKey — prefers typed remark fields. */
export function pickFollowUpRemarkCandidate(raw: Record<string, unknown>): unknown {
  const keys = [
    'follow_up_remark',
    'followUpRemark',
    'follow_up_remark_type',
    'follow_up_type',
    'followUpType'
  ];
  for (const src of getScanNestedSources(raw)) {
    for (const k of keys) {
      const v = src[k];
      if (v === null || v === undefined) continue;
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim() !== '') return v.trim();
    }
  }
  return undefined;
}

const formatDateTimeGb = (d: Date): string =>
  `${d.toLocaleDateString('en-GB')} | ${d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })}`;

function hasParsableTimeInDateString(dateStr: string): boolean {
  return (
    /T\d{2}:\d{2}/.test(dateStr) || (/\s/.test(dateStr) && /\d{1,2}:\d{2}/.test(dateStr))
  );
}

/**
 * Human-readable scan date and time from scan API payload (handles ISO datetime-only fields and separate date/time).
 */
export function formatScanDateTimeLine(raw: Record<string, unknown>): string {
  const timeStr = pickScanField(raw, [
    'scan_time',
    'scanTime',
    'appointment_time',
    'appointmentTime',
    'scan_slot_time',
    'scan_slot_time_display'
  ]);
  const dateStr = pickScanField(raw, [
    'scan_date',
    'scanDate',
    'appointment_date',
    'appointmentDate',
    'scheduled_date',
    'scheduledDate'
  ]);

  if (dateStr) {
    const d = new Date(dateStr);
    if (!Number.isNaN(d.getTime())) {
      if (hasParsableTimeInDateString(dateStr)) {
        return formatDateTimeGb(d);
      }
      if (timeStr && /\d{1,2}:\d{2}/.test(timeStr)) {
        const datePart = dateStr.split('T')[0].trim();
        const combined = new Date(`${datePart}T${timeStr}`);
        if (!Number.isNaN(combined.getTime())) {
          return formatDateTimeGb(combined);
        }
        return `${d.toLocaleDateString('en-GB')} | ${timeStr}`;
      }
      return d.toLocaleDateString('en-GB');
    }
    if (timeStr) {
      return `${dateStr} | ${timeStr}`;
    }
    return dateStr;
  }

  const combinedKeys = [
    'scan_datetime',
    'scanDateTime',
    'appointment_at',
    'appointmentAt',
    'scheduled_at',
    'scheduledAt',
    'booking_scan_at',
    'scan_at'
  ];
  for (const k of combinedKeys) {
    const v = raw[k];
    if (v != null && String(v).trim() !== '') {
      const d = new Date(String(v));
      if (!Number.isNaN(d.getTime())) {
        return formatDateTimeGb(d);
      }
    }
  }

  return '-';
}
